import type { MessageKey } from "@/lib/i18n/messages";

// Maps an avatar part's English `name` to its i18n key. Only descriptive parts
// (skin tones, colours, basics, accessory types) are translated; player/team
// jerseys are proper nouns and intentionally absent so they fall through to the
// raw name. Shared by the store and the avatar picker so both translate alike.
const PART_NAME_KEY_MAP: Record<string, MessageKey> = {
  "Retro Playmaker Home": "wlRewards.jerseyHome",
  "Retro Playmaker Away": "wlRewards.jerseyAway",
  "Retro Playmaker Training": "wlRewards.jerseyTraining",
  "Chelsea": "store.collection_jersey_chelsea",
  "Tottenham": "store.collection_jersey_tottenham",
  "Benfica": "store.collection_jersey_benfica",
  "Porto": "store.collection_jersey_porto",
  "Sporting": "store.collection_jersey_sporting",
  "Marseille": "store.collection_jersey_marseille",
  "Galatasaray": "store.collection_jersey_galatasaray",
  "Fenerbahçe": "store.collection_jersey_fenerbahce",
  "Boca Juniors": "store.collection_jersey_boca_juniors",
  "River Plate": "store.collection_jersey_river_plate",
  "Portugal": "store.collection_jersey_portugal",
  "Spain": "store.collection_jersey_spain",
  "Croatia": "store.collection_jersey_croatia",
  "Japan": "store.collection_jersey_japan",
  "Morocco": "store.collection_jersey_morocco",
  "Nigeria": "store.collection_jersey_nigeria",
  "Mexico": "store.collection_jersey_mexico",
  "Uruguay": "store.collection_jersey_uruguay",
  "Retro Goalkeeper": "store.collection_jersey_retro_keeper",
  "Neon Training": "store.collection_jersey_neon_training",
  "Street Football": "store.collection_jersey_street_football",
  "Gold Champion": "store.collection_jersey_gold_champion",
  "Ronaldinho": "store.collection_hair_ronaldinho",
  "Neymar Mohawk": "store.collection_hair_neymar_mohawk",
  "Zidane": "store.collection_hair_zidane",
  "Haaland": "store.collection_hair_haaland",
  "Beckham Mohawk": "store.collection_hair_beckham_mohawk",
  "Valderrama": "store.collection_hair_valderrama",
  "Gullit": "store.collection_hair_gullit",
  "Baggio": "store.collection_hair_baggio",
  "High Afro": "store.collection_hair_high_afro",
  "Braided Bun": "store.collection_hair_braided_bun",
  "Ponytail": "store.collection_hair_ponytail",
  "Messy Fringe": "store.collection_hair_messy_fringe",
  "Mullet": "store.collection_hair_mullet",
  "Spiky Hair": "store.collection_hair_spiky",
  "Shoulder Curls": "store.collection_hair_shoulder_curls",
  "Side Shave": "store.collection_hair_side_shave",
  "Čech Headguard": "store.collection_headwear_cech",
  "Long Beard": "store.collection_facial_long",
  "Chin Goatee": "store.collection_facial_chin_goatee",
  "Stud Earrings": "store.collection_earwear_studs",
  "Hoop Earring": "store.collection_earwear_hoop",
  "Short Twists": "store.collection_hair_short_twists",
  "Blue Sport": "store.collection_glasses_sport_blue",
  "Celtic": "store.collection_jersey_celtic",

  "Wayfarer": "store.partWayfarer",
  "Round Shades": "store.partRoundShades",
  "Aviator": "store.partAviator",
  "Mustache": "store.partMustache",
  "Beard": "store.partBeard",
  "Boy Basic": "store.partBoyBasic",
  "Girl Basic": "store.partGirlBasic",
  "Light": "store.partLight",
  "Tan": "store.partTan",
  "Brown": "store.partBrown",
  "Dark": "store.partDark",
  "Green": "store.partGreen",
  "Blue": "store.partBlue",
  "Yellow": "store.partYellow",
  "Red": "store.partRed",
  "Violet": "store.partViolet",
  "Pink": "store.partPink",
  "Georgia": "store.partGeorgia",
  "Real Madrid": "store.partRealMadrid",
  "Barcelona": "store.partBarcelona",
  "Milan": "store.partMilan",
  "Liverpool": "store.partLiverpool",
  "Bayern": "store.partBayern",
  "PSG": "store.partPsg",
  "Argentina Retro": "store.partArgentinaRetro",
  "Brazil Retro": "store.partBrazilRetro",
  "France Retro": "store.partFranceRetro",
  "Germany Retro": "store.partGermanyRetro",
  "Netherlands Retro": "store.partNetherlandsRetro",
  "Hamsik": "store.partHamsik",
  "Ramos": "store.partRamos",
  "Ronaldo Nazario": "store.partRonaldoNazario",
  "CR7": "store.partCr7",
  "Man United": "store.partManUnited",
  "Arsenal": "store.partArsenal",
  "Man City": "store.partManCity",
  "Newcastle": "store.partNewcastle",
  "Dinamo Tbilisi": "store.partDinamoTbilisi",
  "Dortmund": "store.partDortmund",
  "Italy Home": "store.partItalyHome",
  "Italy Away": "store.partItalyAway",
  "Italy Third": "store.partItalyThird",
  "England Home": "store.partEnglandHome",
  "England Away": "store.partEnglandAway",
  "Atletico Madrid": "store.partAtleticoMadrid",
  "Napoli": "store.partNapoli",
  "Inter": "store.partInter",
  "Roma": "store.partRoma",
  "Juventus": "store.partJuventus",
  "Ajax": "store.partAjax",
  "Mimino": "store.partMimino",
  "Wave": "store.partWave",
  "Curly Crop": "store.partCurlyCrop",
  "Cornrows": "store.partCornrows",
  "Buzz Cut": "store.partBuzzCut",
  "Side Part": "store.partSidePart",
  "Leopard": "store.partLeopard",
  "Handlebar": "store.partHandlebar",
  "Stache & Goatee": "store.partStacheGoatee",
};

/**
 * Translate an avatar part name via `t`, falling back to the original name when
 * there's no mapping or the key is missing a translation. Pass the `t` from
 * `useLocale()`.
 */
export function translatePartName(
  name: string,
  t: (key: MessageKey) => string,
): string {
  const key = PART_NAME_KEY_MAP[name];
  if (!key) return name;
  const translated = t(key);
  return translated === key ? name : translated;
}
